import { isText, pipeInjectProps } from '@udecode/plate-common';
import { encode } from 'html-entities';
import omit from 'lodash/omit';

const elementToHtml = (editor, props) => {
  if (!props.element.type) return props.children;

  const type = editor.plugins.find((plugin) => {
    return (
      plugin.isElement &&
      plugin.serializeHtml !== null &&
      props.element.type === plugin.type
    );
  })?.type;

  if (!type) return props.children;

  return {
    type,
    variable: props.element.variable,
    props: omit(pipeInjectProps(editor, props), [
      'editor',
      'element',
      'className',
    ]),
  };
};

const leafToHtml = (editor, props) => {
  return {
    type: 'span',
    props: omit(pipeInjectProps(editor, props), ['text', 'leaf', 'editor']),
  };
};

const stringifyCSS = (css) => {
  if (!css) return '';
  const style = Object.entries(css)
    .map(
      ([key, value]) =>
        `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`,
    )
    .join(' ');

  return ` style="${style}"`;
};

export const stringifyNodes = (nodes) => {
  return (
    nodes
      ?.map?.((node) => {
        const { type, variable, children, text } = node || {};
        if (type === 'variable') return `{{${variable}}}`;
        if (children) return stringifyNodes(children);
        return encode(text || '');
      })
      .join('') || ''
  );
};

const stringifyNodesToHtml = (nodes) => {
  if (typeof nodes === 'string') return nodes;
  return nodes
    .map((node) => {
      const {
        type,
        variable,
        props: { children, style },
      } = node;
      if (type === 'variable') {
        const style = children[0].props.style;
        return `<span${stringifyCSS(style)}>{{${variable}}}</span>`;
      }
      const stringifiedChildren = stringifyNodesToHtml(children);
      return `<${type}${stringifyCSS(style)}>${stringifiedChildren}</${type}>`;
    })
    .join('');
};

const adapt = (editor, children) => {
  return children.map((node) => {
    if (isText(node)) {
      return leafToHtml(editor, {
        text: node,
        leaf: node,
        children: encode(node.text),
        editor,
      });
    }

    return elementToHtml(editor, {
      element: node,
      children: adapt(editor, node.children),
      editor,
    });
  });
};

const serializeHtml = (editor, children) => {
  return stringifyNodesToHtml(adapt(editor, children)).replace(
    /\n|<p><span><\/span><\/p>/g,
    '<br>',
  );
};

export const adaptChatVariables = (content, context) => {
  const doubleBraceRegex = /{{(\d+|[a-z$_][\w\-$]*?(?:\.[\w\-$]*?)*?)}}/gi;
  return content.replace(doubleBraceRegex, (_, key) => {
    const variable = context[key];
    return `<span class="bg-taxes-100 px-1 rounded inline-block leading-tight ring-1 ring-taxes-700">${
      variable?.value || '_'
    }</span>`;
  });
};

export const convertVariablesToHtml = (html, context) => {
  // https://github.com/sindresorhus/pupa/blob/main/index.js#LL40C2-L41C75
  // The regex tries to match either a number inside `{{ }}` or a valid JS identifier or key path.
  const regex = /{{(\d+|[a-z$_][\w\-$]*?(?:\.[\w\-$]*?)*?)}}/gi;
  return html.replace(regex, (_, key) => {
    return `<variable data-value="${encode(
      context[key]?.value ?? 'NOT_FOUND',
    )}" data-tooltip="${
      context[key]?.tooltip || ''
    }" data-variable="${key}"></variable>`;
  });
};

export const adaptLegacyVariables = (str) => {
  const dict = {
    '[ amount ]': '{{totalAmountInclVAT}}',
    '[ montant ]': '{{totalAmountInclVAT}}',
    '[ bedrag ]': '{{totalAmountInclVAT}}',
    '[ fullname ]': '{{fullname}}',
    '[ nomcomplet ]': '{{fullname}}',
    '[ volledigenaam ]': '{{fullname}}',
  };

  const keysExp = new RegExp(
    Object.keys(dict)
      .join('|')
      .replace(/[[\]]/g, (bracket) => '\\' + bracket),
    'g',
  );

  const newStr = str?.replace?.(keysExp, (matched) => {
    return dict[matched];
  });

  return newStr;
};

export default serializeHtml;
